import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { addAsyncCases } from 'shared/utils/redux';
import { PerspectivesListModel } from 'api/dto/new/Perspectives/PerspectiveListDto';
import { CustomAvatarModel } from 'api/dto/new/Perspectives/CustomAvatar.dto';
import { Resource } from 'model/Resource';
import { PerspectiveSummaryModel } from 'api/dto/new/Perspectives/PerspectiveSummary.dto';
import { fetchPerspectiveAIAvatar } from 'reduxStore/ai/asyncActions';
import { PerspectivesDepartmentModel } from 'api/dto/new/Perspectives/DepartmentsListResult.dto';
import { PerspectiveAccessMap } from 'shared/utils/perspectiveAccessStorage';

import { PerspectivesState, STORE_NAME } from './initialState';
import {
  fetchCustomAvatars,
  fetchPerspective,
  fetchPerspectiveDepartments,
  fetchPerspectivesList,
  fetchPerspectiveSummary,
  fetchPerspectiveTopics,
} from './asyncActions';

export const perspectivesSlice = createSlice({
  name: STORE_NAME,
  initialState: PerspectivesState.INITIAL_DOMAIN,
  reducers: {
    setCustomAvatars: (state, action: PayloadAction<CustomAvatarModel[]>) => {
      state.customAvatars = Resource.createResolvedResource({ data: action.payload });
    },
    setPerspectiveSummary: (state, action: PayloadAction<PerspectiveSummaryModel>) => {
      state.summary = Resource.createResolvedResource({ data: action.payload });
    },
    setPerspectivesList: (state, action: PayloadAction<PerspectivesListModel>) => {
      state.list = Resource.createResolvedResource({ data: action.payload });
    },
    setPerspectivesDepartments: (state, action: PayloadAction<PerspectivesDepartmentModel>) => {
      state.departments = Resource.createResolvedResource({ data: action.payload });
    },
    setPerspectiveAccessMap: (state, action: PayloadAction<PerspectiveAccessMap>) => {
      state.accessMap = action.payload;
    },
  },
  extraReducers: (builder) => {
    addAsyncCases({
      builder,
      asyncActionCreator: fetchPerspectivesList,
      reducer: (state, action) => {
        state.list = action.payload;
      },
    });

    addAsyncCases({
      builder,
      asyncActionCreator: fetchPerspective,
      reducer: (state, action) => {
        state.selected = action.payload;
      },
    });

    addAsyncCases({
      builder,
      asyncActionCreator: fetchPerspectiveTopics,
      reducer: (state, action) => {
        state.topics = action.payload;
      },
    });

    addAsyncCases({
      builder,
      asyncActionCreator: fetchPerspectiveDepartments,
      reducer: (state, action) => {
        state.departments = action.payload;
      },
    });

    addAsyncCases({
      builder,
      asyncActionCreator: fetchCustomAvatars,
      reducer: (state, action) => {
        state.customAvatars = action.payload;
      },
    });

    addAsyncCases({
      builder,
      asyncActionCreator: fetchPerspectiveSummary,
      reducer: (state, action) => {
        state.summary = action.payload;
      },
    });

    addAsyncCases({
      builder,
      asyncActionCreator: fetchPerspectiveAIAvatar,
      reducer: (state, action) => {
        state.aiAvatar = action.payload;
      },
    });
  },
});

export const {
  setCustomAvatars,
  setPerspectiveSummary,
  setPerspectivesList,
  setPerspectivesDepartments,
  setPerspectiveAccessMap,
} = perspectivesSlice.actions;
